import { View, RadioGroup, Label, YStack } from 'tamagui'
import { QuestionWrapper } from './QuestionWrapper'
import { useFormContext, Controller } from 'react-hook-form'
import { useCallback, useMemo } from 'react'

interface ITrueFalseQuestionProps {
  questionId: string
  previousAnswer?: {
    selectedOption: boolean
    correct: boolean
    score: number
  }
  mode: 'edit' | 'readonly' | 'retry'
}

export function TrueFalseQuestion({
  questionId,
  previousAnswer,
  mode,
}: ITrueFalseQuestionProps) {
  const { control } = useFormContext()

  const getColor = useCallback(
    (value: string, ref: string, isTransparent = false) => {
      if (value === ref) {
        if (!previousAnswer) return '$accent'
        if (previousAnswer?.correct) return '$success-300'
        return '$danger-300'
      }

      return isTransparent ? 'transparent' : '$control-inactive'
    },
    [previousAnswer],
  )

  const defaultValueOption = useMemo(() => {
    if (previousAnswer?.selectedOption === undefined) return undefined
    return previousAnswer?.selectedOption ? 'true' : 'false'
  }, [previousAnswer])

  return (
    <View>
      <Controller
        name={questionId}
        control={control}
        defaultValue={defaultValueOption}
        render={({ field: { onChange, value } }) => (
          <RadioGroup
            name={`true-false-${questionId}`}
            onValueChange={mode === 'edit' ? onChange : undefined}
            value={value}
            disabled={mode === 'readonly' || mode === 'retry'}
          >
            <YStack gap="$spacing-inset-nano">
              {/* Opção Verdadeiro */}
              <QuestionWrapper color={getColor(value, 'true')}>
                <View
                  flexDirection="row"
                  alignItems="center"
                  flex={1}
                  minWidth={0}
                >
                  <RadioGroup.Item
                    value="true"
                    id={`true-${questionId}`}
                    borderColor="$border-default"
                    backgroundColor={getColor(value, 'true', true)}
                    size={'$spacing-lg'}
                  >
                    <RadioGroup.Indicator
                      borderWidth={1}
                      w={8}
                      h={8}
                      borderColor={getColor(value, 'true')}
                      backgroundColor={
                        value === 'true' ? '$bg-surface-raised' : 'transparent'
                      }
                    />
                  </RadioGroup.Item>
                  <Label
                    color="$text-primary"
                    fontFamily="$mulishRegular"
                    size={'$font-size-xxs'}
                    htmlFor={`true-${questionId}`}
                    p="$spacing-inset-xs"
                    flex={1}
                    minWidth={0}
                    flexShrink={1}
                  >
                    Verdadeiro
                  </Label>
                </View>
              </QuestionWrapper>

              {/* Opção Falso */}
              <QuestionWrapper color={getColor(value, 'false')}>
                <View
                  flexDirection="row"
                  alignItems="center"
                  flex={1}
                  minWidth={0}
                >
                  <RadioGroup.Item
                    value="false"
                    id={`false-${questionId}`}
                    borderColor="$border-default"
                    backgroundColor={getColor(value, 'false', true)}
                    size={'$spacing-lg'}
                  >
                    <RadioGroup.Indicator
                      borderWidth={1}
                      w={8}
                      h={8}
                      borderColor={getColor(value, 'false')}
                      backgroundColor={
                        value === 'false' ? '$bg-surface-raised' : 'transparent'
                      }
                    />
                  </RadioGroup.Item>
                  <Label
                    color="$text-primary"
                    fontFamily="$mulishRegular"
                    size={'$font-size-xxs'}
                    htmlFor={`false-${questionId}`}
                    p="$spacing-inset-xs"
                    flex={1}
                    minWidth={0}
                    flexShrink={1}
                  >
                    Falso
                  </Label>
                </View>
              </QuestionWrapper>
            </YStack>
          </RadioGroup>
        )}
      />
    </View>
  )
}
